import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import t from "@/lang/lang";
import {
  DataTable,
  useDataTableController,
} from "@/shared/components/tables/data-table";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { Plus, RefreshCw, Trash2 } from "lucide-react";
import { ColumnDef } from "@tanstack/react-table";
import { ComponentKind } from "@/services/backend/htz/ahu/types/component-kind";
import { Button } from "@/shared/components/ui/button";
import {
  ListRequest,
  useHtzAhuComponentKindDeleteMutation,
  useHtzAhuComponentKindListQuery,
  useHtzAhuComponentKindPutMutation,
} from "@/services/backend/htz/ahu/api/component-kind-api";
import { useToast } from "@/shared/hooks/use-toast";
import { useEffect, useMemo, useState } from "react";
import { CompanyFilter } from "@/services/backend/company/components/company-filter";
import { CompanyName } from "@/services/backend/company/components/company-name";
import { CompanySelect } from "@/services/backend/company/components/company-select";
import { parseRTKQueryError } from "@/shared/components/errors/parse-r-t-k-query-error";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { v4 } from "uuid";
import { Label } from "@/shared/components/ui/label";
import { Input } from "@/shared/components/ui/input";
import { InputValidationErrors } from "@/shared/components/errors/input-error-messages";

export function ComponentKindsTable() {
  const [request, setRequest] = useState<ListRequest>({
    company: { active: false, values: [] },
  });

  const {
    data: list,
    isLoading,
    error,
  } = useHtzAhuComponentKindListQuery(request);

  // The company filter is applied client-side as well. The list endpoint is
  // not paginated and the backend already scopes the result to the companies
  // the user is associated with, so narrowing here is safe and keeps the
  // filter working independently of the server-side filter support.
  const companyFilter = request.company;
  const componentKinds = useMemo(() => {
    const kinds = list?.kinds ?? [];
    const values = companyFilter?.values ?? [];
    if (!companyFilter?.active || values.length === 0) {
      return kinds;
    }
    return kinds.filter((kind) => values.includes(kind.companyId));
  }, [list?.kinds, companyFilter]);

  const table = useDataTableController(tableColumns, componentKinds);

  if (isLoading) {
    return <Skeleton />;
  }

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Arten von Anlagenkomponenten")}</CardTitle>
        <CardDescription>
          {t(
            "Hier können verschiedene Arten von Anlagenkomponenten definiert werden. Diese Arten werden genutzt um definierten Komponenten die Vorlagen für konstruktive und visuelle Beurteilungen zuzuordnen.",
          )}
        </CardDescription>
        <CardAction>
          <CreateComponentKindDialog />
        </CardAction>
      </CardHeader>
      <CardContent className="grid gap-2">
        <div className="flex items-center gap-2">
          <CompanyFilter
            filter={request.company ?? { active: false, values: [] }}
            onFilterChange={(company) => setRequest({ ...request, company })}
          />
        </div>
        <DataTable
          table={table}
          columns={tableColumns}
          className="border-none"
        />
      </CardContent>
    </Card>
  );
}

const tableColumns: ColumnDef<ComponentKind>[] = [
  {
    accessorKey: "name",
    header: t("Name"),
  },
  {
    id: "company",
    header: t("Unternehmen"),
    cell: ({ row }) => <CompanyName companyId={row.original.companyId} />,
  },
  {
    id: "actions",
    cell: ({ row }) => (
      <div className="flex justify-end">
        <DeleteComponentKindButton componentKindId={row.original.id} />
      </div>
    ),
  },
];

function DeleteComponentKindButton({
  componentKindId,
}: {
  componentKindId: string;
}) {
  const [del, { isLoading, error, reset }] =
    useHtzAhuComponentKindDeleteMutation();
  const { toast } = useToast();

  useEffect(() => {
    if (error) {
      toast({
        ...parseRTKQueryError(error),
        variant: "destructive",
      });
      reset();
    }
  }, [error, reset, toast]);

  const doDelete = () => {
    if (isLoading) {
      return;
    }

    del({ id: componentKindId });
  };

  return (
    <Button onClick={doDelete} variant="destructive">
      <Trash2 className="h-5 w-5" />
    </Button>
  );
}

function CreateComponentKindDialog() {
  const [open, setOpen] = useState<boolean>(false);
  const [request, setRequest] = useState<ComponentKind>({
    id: "",
    name: "",
    companyId: "",
  });

  const [put, { isLoading, error, reset, isSuccess }] =
    useHtzAhuComponentKindPutMutation();

  if (isSuccess) {
    reset();
    setOpen(false);
    setRequest({ id: "", name: "", companyId: "" });
  }

  const doCreate = () => {
    if (isLoading) {
      return;
    }

    put({
      ...request,
      id: v4(),
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="mr-2 h-5 w-5" />
          <span>{t("Art")}</span>
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("Art Erstellen")}</DialogTitle>
          <DialogDescription>
            {t("Der Name muss eindeutig sein.")}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid content-start gap-1.5">
            <Label>{t("Name")}</Label>
            <Input
              value={request?.name}
              onChange={(e) => setRequest({ ...request, name: e.target.value })}
            />
            <InputValidationErrors error={error} field="name" />
          </div>
          <div className="grid content-start gap-1.5">
            <Label>{t("Unternehmen")}</Label>
            <CompanySelect
              companyId={request.companyId}
              onCompanyIdChange={(companyId) =>
                setRequest({ ...request, companyId })
              }
            />
            <InputValidationErrors error={error} field="companyId" />
          </div>
        </div>
        <RTKQueryErrorAlert error={error} />
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">{t("Abbrechen")}</Button>
          </DialogClose>
          <Button disabled={isLoading} onClick={doCreate}>
            {t("Erstellen")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Skeleton() {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Arten von Anlagenkomponenten")}</CardTitle>
        <CardDescription>
          {t(
            "Hier können grundsätzlich verschiedene Arten von Anlagenkomponenten definiert werden. Diese Arten werden genutzt um definierten Komponenten die Vorlagen für konstruktive und visuelle Beurteilungen zuzuordnen.",
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <RefreshCw className="animate-spin" />
        <span>{t("Lade Daten...")}</span>
      </CardContent>
    </Card>
  );
}
